'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isEditableShortcutTarget } from '@/lib/utils';
import Mousetrap from 'mousetrap';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  SECRET_IMAGE_EASTER_EGG_SEQUENCE,
  SECRET_IMAGE_EASTER_EGG_SOURCES,
} from './secret-image-easter-egg';

export function SecretImageDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const imageSrc =
    SECRET_IMAGE_EASTER_EGG_SOURCES[imageIndex] ??
    SECRET_IMAGE_EASTER_EGG_SOURCES[0];

  useEffect(() => {
    const handler = (e?: KeyboardEvent) => {
      if (e && isEditableShortcutTarget(e.target)) {
        return false;
      }

      e?.preventDefault();
      setImageIndex(0);
      setImageLoaded(false);
      setIsOpen(true);
      return false;
    };

    Mousetrap.bind(SECRET_IMAGE_EASTER_EGG_SEQUENCE, handler);
    return () => {
      Mousetrap.unbind(SECRET_IMAGE_EASTER_EGG_SEQUENCE);
    };
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="overflow-hidden border-white/10 bg-zinc-950 p-0 shadow-2xl sm:max-w-2xl">
        <DialogHeader className="border-b border-white/10 px-5 py-4">
          <DialogTitle className="text-white">Hidden image</DialogTitle>
          <DialogDescription>
            A secret IMAI image unlocked from the keyboard.
          </DialogDescription>
        </DialogHeader>
        <div className="relative flex min-h-[320px] items-center justify-center bg-black p-4 sm:min-h-[520px]">
          {!imageLoaded ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 backdrop-blur">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading easter egg...
              </div>
            </div>
          ) : null}
          <img
            src={imageSrc}
            alt="Hidden IMAI easter egg"
            className={`max-h-[72vh] w-auto max-w-full rounded-md object-contain shadow-2xl transition-opacity duration-200 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => {
              setImageLoaded(true);
            }}
            onError={() => {
              if (imageIndex < SECRET_IMAGE_EASTER_EGG_SOURCES.length - 1) {
                setImageIndex((current) => current + 1);
                setImageLoaded(false);
                return;
              }

              setImageLoaded(false);
            }}
          />
          {!imageLoaded &&
          imageIndex === SECRET_IMAGE_EASTER_EGG_SOURCES.length - 1 ? (
            <div className="absolute inset-x-6 bottom-6 rounded-xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              Could not load the hidden image. Expected one of:{' '}
              {SECRET_IMAGE_EASTER_EGG_SOURCES.join(', ')}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
